import { useEffect, useState, useMemo } from 'react'
import { Plus, Pencil, Trash2, RotateCcw, QrCode, Users, Search, Phone, FileText, ShieldCheck, CalendarX } from 'lucide-react'
import clsx from 'clsx'
import {
  tumPersonelleriGetir,
  personelSil,
  personelGeriYukle,
} from '@/lib/services/personel-service'
import { ayrilanPersonelVardiyalariniIptalEt } from '@/lib/services/vardiya-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PersonelFormModal } from './PersonelFormModal'
import { SetupTokenModal } from './SetupTokenModal'
import { BelgelerModal } from '@/components/belge/BelgelerModal'
import type { Vardiyali, Pozisyon, Sube } from '@/types'

type RolFiltre = 'tumu' | 'normal' | 'yonetici'

export function PersonelPage() {
  const [personeller, setPersoneller] = useState<Vardiyali[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [pasifGoster, setPasifGoster] = useState(false)
  const [arama, setArama] = useState('')
  const [rolFiltre, setRolFiltre] = useState<RolFiltre>('tumu')

  const [formAcik, setFormAcik] = useState(false)
  const [duzenlenecek, setDuzenlenecek] = useState<Vardiyali | null>(null)
  const [silinecek, setSilinecek] = useState<Vardiyali | null>(null)

  const [qrAcik, setQrAcik] = useState(false)
  const [qrPersonelId, setQrPersonelId] = useState<string | null>(null)
  const [qrPersonelIsim, setQrPersonelIsim] = useState('')

  const [belgelerAcik, setBelgelerAcik] = useState(false)
  const [belgelerPersonel, setBelgelerPersonel] = useState<Vardiyali | null>(null)

  // Ayrılan personel: planlı vardiya iptali
  const [vardiyaIptalHedef, setVardiyaIptalHedef] = useState<Vardiyali | null>(null)
  const [vardiyaIptalYukleniyor, setVardiyaIptalYukleniyor] = useState(false)
  const [vardiyaIptalMesaj, setVardiyaIptalMesaj] = useState<string | null>(null)

  async function vardiyaIptalOnayla() {
    if (!vardiyaIptalHedef) return
    setVardiyaIptalYukleniyor(true)
    try {
      const sonuc = await ayrilanPersonelVardiyalariniIptalEt(vardiyaIptalHedef.id)
      setVardiyaIptalMesaj(
        `${vardiyaIptalHedef.isim}: ${sonuc.iptalEdilen} planlı vardiya iptal edildi. ` +
        `${sonuc.korunan} çalışılmış/korunan vardiyaya dokunulmadı.`
      )
    } catch (err) {
      setVardiyaIptalMesaj('İşlem başarısız: ' + (err as Error).message)
    } finally {
      setVardiyaIptalYukleniyor(false)
      setVardiyaIptalHedef(null)
    }
  }

  async function yukle() {
    try {
      setYukleniyor(true)
      const [p, poz, sub] = await Promise.all([
        tumPersonelleriGetir(),
        tumPozisyonlariGetir(),
        tumSubeleriGetir(),
      ])
      setPersoneller(p)
      setPozisyonlar(poz)
      setSubeler(sub)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Personeller yüklenemedi.')
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
  }, [])

  const pozisyonHaritasi = useMemo(() => {
    const m = new Map<string, string>()
    pozisyonlar.forEach((p) => m.set(p.id, p.isim))
    return m
  }, [pozisyonlar])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, string>()
    subeler.forEach((s) => m.set(s.id, s.isim))
    return m
  }, [subeler])

  async function silOnay() {
    if (!silinecek) return
    try {
      await personelSil(silinecek.id)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  async function geriYukleP(p: Vardiyali) {
    try {
      await personelGeriYukle(p.id)
      await yukle()
    } catch (err) {
      alert('Geri yüklenemedi: ' + (err as Error).message)
    }
  }

  function qrGoster(p: Vardiyali) {
    setQrPersonelId(p.id)
    setQrPersonelIsim(p.isim)
    setQrAcik(true)
  }

  function belgeleriGoster(p: Vardiyali) {
    setBelgelerPersonel(p)
    setBelgelerAcik(true)
  }

  const sayilar = useMemo(() => {
    const aktifler = personeller.filter(p => p.aktif)
    return {
      tumu: aktifler.length,
      normal: aktifler.filter(p => !p.yoneticiMi).length,
      yonetici: aktifler.filter(p => p.yoneticiMi).length,
    }
  }, [personeller])

  const gosterilen = useMemo(() => {
    let liste = pasifGoster ? personeller : personeller.filter((p) => p.aktif)
    if (rolFiltre === 'normal') liste = liste.filter(p => !p.yoneticiMi)
    if (rolFiltre === 'yonetici') liste = liste.filter(p => p.yoneticiMi)
    if (arama.trim()) {
      const q = arama.trim().toLowerCase()
      liste = liste.filter(
        (p) => p.isim.toLowerCase().includes(q) || p.telefon.includes(q)
      )
    }
    return liste
  }, [personeller, pasifGoster, arama, rolFiltre])

  const referansEksik = pozisyonlar.length === 0 || subeler.length === 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personel</h1>
          <p className="text-sm text-gray-600 mt-1">
            {gosterilen.length} kayıt
            {!pasifGoster && personeller.length > gosterilen.length && (
              <button
                onClick={() => setPasifGoster(true)}
                className="ml-2 text-brand-600 hover:underline"
              >
                ({personeller.filter((p) => !p.aktif).length} pasif gizli)
              </button>
            )}
            {pasifGoster && (
              <button
                onClick={() => setPasifGoster(false)}
                className="ml-2 text-brand-600 hover:underline"
              >
                (sadece aktifler)
              </button>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            setDuzenlenecek(null)
            setFormAcik(true)
          }}
          className="btn-primary flex items-center gap-2"
          disabled={referansEksik}
          title={referansEksik ? 'Önce şube ve pozisyon ekleyin' : ''}
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Yeni Personel</span>
        </button>
      </div>

      {referansEksik && !yukleniyor && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 mb-4">
          <p className="font-semibold mb-1">⚠️ Önce hazırlık yap</p>
          <p className="text-sm">
            Personel eklemeden önce <strong>en az 1 şube</strong> ve <strong>en az 1 pozisyon</strong> tanımlamış olmalısın.
            {pozisyonlar.length === 0 && ' (Pozisyon yok)'}
            {subeler.length === 0 && ' (Şube yok)'}
          </p>
        </div>
      )}

      {!yukleniyor && personeller.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            <FiltreChip
              aktif={rolFiltre === 'tumu'}
              onClick={() => setRolFiltre('tumu')}
              label={`Tümü (${sayilar.tumu})`}
            />
            <FiltreChip
              aktif={rolFiltre === 'normal'}
              onClick={() => setRolFiltre('normal')}
              label={`Personel (${sayilar.normal})`}
            />
            <FiltreChip
              aktif={rolFiltre === 'yonetici'}
              onClick={() => setRolFiltre('yonetici')}
              label={`Yönetici (${sayilar.yonetici})`}
              icon={<ShieldCheck className="w-3.5 h-3.5" />}
            />
          </div>
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              placeholder="İsim veya telefon ile ara..."
              className="input-base pl-10"
            />
          </div>
        </>
      )}

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-4">
          {hata}
        </div>
      )}

      {yukleniyor && (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      )}

      {!yukleniyor && gosterilen.length === 0 && !referansEksik && (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">
            {arama ? 'Eşleşen personel yok' : 'Henüz personel yok'}
          </h3>
          {!arama && (
            <>
              <p className="text-gray-600 mb-6">
                İlk personeli eklemek için yukarıdaki butonu kullan.
              </p>
              <button
                onClick={() => {
                  setDuzenlenecek(null)
                  setFormAcik(true)
                }}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                İlk Personeli Ekle
              </button>
            </>
          )}
        </div>
      )}

      {!yukleniyor && gosterilen.length > 0 && (
        <div className="space-y-3">
          {gosterilen.map((p) => (
            <PersonelKarti
              key={p.id}
              personel={p}
              pozisyonHaritasi={pozisyonHaritasi}
              subeHaritasi={subeHaritasi}
              onDuzenle={() => {
                setDuzenlenecek(p)
                setFormAcik(true)
              }}
              onSil={() => setSilinecek(p)}
              onGeriYukle={() => geriYukleP(p)}
              onVardiyaIptal={() => setVardiyaIptalHedef(p)}
              onQR={() => qrGoster(p)}
              onBelgeler={() => belgeleriGoster(p)}
            />
          ))}
        </div>
      )}

      <PersonelFormModal
        acik={formAcik}
        personel={duzenlenecek}
        onKapat={() => setFormAcik(false)}
        onKaydedildi={(yeniId) => {
          setFormAcik(false)
          yukle()
          if (yeniId) {
            const yeni = personeller.find((p) => p.id === yeniId)
            setQrPersonelId(yeniId)
            setQrPersonelIsim(yeni?.isim ?? 'Yeni personel')
            setQrAcik(true)
            tumPersonelleriGetir().then((list) => {
              const guncel = list.find((x) => x.id === yeniId)
              if (guncel) setQrPersonelIsim(guncel.isim)
            })
          }
        }}
      />

      <SetupTokenModal
        acik={qrAcik}
        personelId={qrPersonelId}
        personelIsim={qrPersonelIsim}
        onKapat={() => setQrAcik(false)}
      />

      <BelgelerModal
        acik={belgelerAcik}
        personel={belgelerPersonel}
        onKapat={() => setBelgelerAcik(false)}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Personeli Pasif Yap"
        mesaj={`"${silinecek?.isim}" pasif duruma alınacak. Geçmiş kayıtlar (vardiya, izin, maaş) korunur ama yeni vardiya atanamaz. Devam mı?`}
        onayMetni="Pasif Yap"
        tehlikeli
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />

      <ConfirmDialog
        acik={!!vardiyaIptalHedef}
        baslik="Planlı Vardiyaları İptal Et"
        mesaj={`"${vardiyaIptalHedef?.isim}" ayrıldı olarak işaretlenecek: çalışılmamış (giriş kaydı olmayan) planlı vardiyaları iptal edilecek. Çalıştığı günlerin vardiyaları KORUNUR (maaş için). Vardiyalar silinmez, sadece iptal olur. Devam mı?`}
        onayMetni={vardiyaIptalYukleniyor ? 'İşleniyor...' : 'İptal Et'}
        onOnay={vardiyaIptalOnayla}
        onIptal={() => setVardiyaIptalHedef(null)}
      />

      <ConfirmDialog
        acik={!!vardiyaIptalMesaj}
        baslik="Tamamlandı"
        mesaj={vardiyaIptalMesaj ?? ''}
        onayMetni="Tamam"
        onOnay={() => { setVardiyaIptalMesaj(null); yukle() }}
        onIptal={() => { setVardiyaIptalMesaj(null); yukle() }}
      />
    </div>
  )
}

interface FiltreChipProps {
  aktif: boolean
  onClick: () => void
  label: string
  icon?: React.ReactNode
}

function FiltreChip({ aktif, onClick, label, icon }: FiltreChipProps) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors',
        aktif
          ? 'bg-brand-600 text-white'
          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
      )}
    >
      {icon}
      {label}
    </button>
  )
}

interface PersonelKartiProps {
  personel: Vardiyali
  pozisyonHaritasi: Map<string, string>
  subeHaritasi: Map<string, string>
  onDuzenle: () => void
  onSil: () => void
  onGeriYukle: () => void
  onVardiyaIptal: () => void
  onQR: () => void
  onBelgeler: () => void
}

function PersonelKarti({
  personel, pozisyonHaritasi, subeHaritasi,
  onDuzenle, onSil, onGeriYukle, onVardiyaIptal, onQR, onBelgeler,
}: PersonelKartiProps) {
  const pozisyonAdlari = personel.pozisyonIdleri
    .map((id) => pozisyonHaritasi.get(id))
    .filter(Boolean) as string[]
  const subeAdlari = personel.subeIdleri
    .map((id) => subeHaritasi.get(id))
    .filter(Boolean) as string[]

  return (
    <div className={`card ${!personel.aktif ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="font-bold text-gray-900 text-lg truncate">{personel.isim}</h3>
            {!personel.aktif && (
              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                Pasif
              </span>
            )}
            {personel.yoneticiMi && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-semibold"
                title={personel.email ?? ''}
              >
                <ShieldCheck className="w-3 h-3" />
                Yönetici{personel.email ? ` (${personel.email})` : ''}
              </span>
            )}
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
              {personel.calismaTipi === 'fulltime' ? 'Tam Zamanlı' : 'Yarı Zamanlı'}
            </span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
              {personel.maasTipi === 'aylik' ? 'Aylık' : 'Saatlik'}
            </span>
          </div>

          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 flex-shrink-0 text-gray-400" />
              <span>{personel.telefon}</span>
            </div>
            {pozisyonAdlari.length > 0 && (
              <div className="text-sm">
                <span className="text-gray-500">Pozisyon:</span>{' '}
                <span className="text-gray-700">{pozisyonAdlari.join(', ')}</span>
              </div>
            )}
            {subeAdlari.length > 0 && (
              <div className="text-sm">
                <span className="text-gray-500">Şube:</span>{' '}
                <span className="text-gray-700">{subeAdlari.join(', ')}</span>
              </div>
            )}
            <div className="text-sm">
              <span className="text-gray-500">Ücret:</span>{' '}
              <span className="text-gray-700">
                {personel.ucret.toLocaleString('de-DE')} ₺
                {personel.maasTipi === 'saatlik' ? '/saat' : '/ay'}
              </span>
            </div>
          </div>
        </div>

        {/* Avatar */}
        {(() => {
          const url = (personel as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl
          return url ? (
            <img
              src={url}
              alt={personel.isim}
              className="w-28 h-28 rounded-full object-cover border-2 border-gray-200 flex-shrink-0 self-center"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0 self-center">
              {personel.isim.charAt(0).toUpperCase()}
            </div>
          )
        })()}

        <div className="flex flex-col gap-1.5">
          {personel.aktif ? (
            <>
              <button
                onClick={onQR}
                className="p-2 hover:bg-brand-50 rounded-lg text-gray-600 hover:text-brand-600 transition-colors"
                title="Setup QR"
              >
                <QrCode className="w-4 h-4" />
              </button>
              <button
                onClick={onBelgeler}
                className="p-2 hover:bg-purple-50 rounded-lg text-gray-600 hover:text-purple-600 transition-colors"
                title="Belgeler"
              >
                <FileText className="w-4 h-4" />
              </button>
              <button
                onClick={onDuzenle}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
                title="Düzenle"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={onSil}
                className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
                title="Pasif Yap"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onVardiyaIptal}
                className="p-2 hover:bg-amber-50 rounded-lg text-gray-600 hover:text-amber-600 transition-colors"
                title="Ayrıldı: planlı vardiyaları iptal et"
              >
                <CalendarX className="w-4 h-4" />
              </button>
              <button
                onClick={onGeriYukle}
                className="p-2 hover:bg-green-50 rounded-lg text-gray-600 hover:text-green-600 transition-colors"
                title="Geri Yükle"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
